import type { ReactNode } from 'react'

import { Layout, Seo } from '@/components/generals'

const WhatWeDoPage = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Coaching Programs for Orthodontists'
                description='Annual one-on-one coaching, 90-minute power sessions, merchant rate negotiation, coordinator training and a 3D printing masterclass for orthodontists.'
            />

            <>
                <div className='hero-section-secondary overflow-hidden' id='hero'>
                    <div className='container'>
                        <div className='hero-content'>
                            <div className='mdl-layout-grid who-we-are-hero-grid'>
                                <div className='content relative zindex-3' id='mdl-node-_982a1847-4b52-d2d4-b324-6e0ccd27a8b7-68628a74'>
                                    <h1 className='hero-text-content no-margins'>
                                        <div className='hero-text-word'>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _1'>Real</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _2'>Talk,</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _3'>Real</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _4'>Strategy,</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _5'>Real</div>
                                            </div>
                                            <div className='hero-text-letter'>
                                                <div className='hero-text _6'>Results</div>
                                            </div>
                                        </div>
                                    </h1>
                                    <div className='margin-top-1em'>
                                        <div className='animation-on-page-load-1'>
                                            <div className='hero-description'>
                                                No fluff and no cookie-cutter plans. Whether it&apos;s comprehensive annual
                                                coaching or a targeted session, every service is finely tuned to your
                                                practice, your team and the life you want to build.
                                            </div>
                                        </div>
                                        <div className='animation-on-page-load-2'>
                                            <div className='hero-buttons-holder'>
                                                <a className='button mdl-button' href='/contact'>let&apos;s talk</a>
                                                <a className='button button-transparent mdl-inline-block' href='/showcases'>
                                                    <div>Client stories</div>
                                                    <span className='button-icon' aria-hidden='true' />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='content relative animation-on-page-load-3' id='mdl-node-_982a1847-4b52-d2d4-b324-6e0ccd27a8c2-68628a74'>
                                    <div className='what-we-do-images-container'>
                                        <div className='what-we-do-image-holder'>
                                            <img
                                                className='what-we-do-image'
                                                src='/images/czechmate/chess-board.jpg'
                                                alt='Blue chess pieces standing over a fallen white king on a chessboard'
                                            />
                                            <div className='what-we-do-image-overlay' />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Client logo row hidden: the real site shows no client logos. */}
                    </div>
                </div>

                <div className='section overflow-hidden' id='results'>
                    <div className='container'>
                        <div className='mdl-layout-grid grid-2-col'>
                            <div className='content' id='mdl-node-a823d78d-020b-026d-e2e0-c0d090ceacb6-68628a74'>
                                <div className='fade-in-move-on-scroll'>
                                    <h2 className='no-margins white-text'>Proven in a Real Practice</h2>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-a823d78d-020b-026d-e2e0-c0d090ceacba-68628a74'>
                                <div className='fade-in-move-on-scroll'>
                                    <div className='white-text'>
                                        Every script, system and schedule we teach comes from the practice Brad built
                                        over 23 years, and from the trainers who helped him scale it to three locations.
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='mdl-layout-grid grid-stats on-dark fade-in-on-scroll'>
                            <div className='content' id='mdl-node-a823d78d-020b-026d-e2e0-c0d090ceaca3-68628a74'>
                                <div className='stats-content'>
                                    <div className='big-number white-text'>23</div>
                                    <div className='white-text'>years running his own orthodontic practice</div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-a823d78d-020b-026d-e2e0-c0d090ceaca9-68628a74'>
                                <div className='stats-content'>
                                    <div className='big-number white-text'>90%</div>
                                    <div className='white-text'>case acceptance rate from our treatment coordinator trainer</div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-a823d78d-020b-026d-e2e0-c0d090ceacaf-68628a74'>
                                <div className='stats-content'>
                                    <div className='big-number white-text'>3</div>
                                    <div className='white-text'>doctor days cut from the schedule each month, on average</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='section-background dark-section-bg' />
                </div>

                <div className='section' id='annual-coaching'>
                    <div className='container'>
                        <div className='mdl-layout-grid service-detail-grid'>
                            <div className='content' id='mdl-node-_8e42f1f9-96f1-9679-ebe7-7e6d0eb636c4-68628a74'>
                                <div className='service-detail-content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <img className='service-detail-icon' src='/images/sync-content-refresh-sharie-filder-synchronize-2-1.svg' alt='' />
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='no-margins'>The Czechmate Year: One-on-One Coaching</h2>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <p>
                                            This is the deep dive. The full game board. For one year, you work directly
                                            with Dr. Brad Chvatal to Czechmate your practice and your life, looking at
                                            everything: overhead, scheduling, time worked, stress levels, team morale,
                                            culture, financial health and leadership.
                                        </p>
                                    </div>
                                    <div className='service-detail-2col-grid'>
                                        <div className='content' id='mdl-node-e152d0fd-0147-3b0d-a3cc-708200862de0-68628a74'>
                                            <div className='fade-in-move-on-scroll'>
                                                <div>
                                                    Direct access to Brad and his strategies
                                                    <br />
                                                    Trusted experts, with no markups
                                                </div>
                                            </div>
                                        </div>
                                        <div className='content' id='mdl-node-_0a472daf-65da-8212-21c8-d93f8a28875e-68628a74'>
                                            <div className='fade-in-move-on-scroll'>
                                                <div>
                                                    All four core team trainers
                                                    <br />
                                                    Limited to 10 clients at a time
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_1704e93d-b769-9f85-eedd-09339a747f55-68628a74'>
                                <div className='image-holder-with-animation image-holder-service-detail'>
                                    <div className='image-with-animation-container image-service-detail-container'>
                                        <img
                                            className='image-with-animation'
                                            src='/images/czechmate/brad-portrait.jpg'
                                            alt='Dr. Brad Chvatal smiling in a burgundy sweater against an orange wall'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='section' id='power-session'>
                    <div className='container'>
                        <div className='mdl-layout-grid service-detail-grid'>
                            <div className='content' id='mdl-node-_962754fb-8120-99c3-36d3-e25b0be9e2d9-68628a74'>
                                <div className='image-holder-with-animation image-holder-service-detail'>
                                    <div className='image-with-animation-container image-service-detail-container'>
                                        <img
                                            className='image-with-animation'
                                            src='/images/czechmate/power-session.jpg'
                                            alt='A notebook reading Next move? beside a Win jar and a white chess knight'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_962754fb-8120-99c3-36d3-e25b0be9e2c6-68628a74'>
                                <div className='service-detail-content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <img className='service-detail-icon' src='/images/server.svg' alt='' />
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='no-margins'>90-Minute Power Session</h2>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <p>
                                            Sometimes you don&apos;t need a year-long plan. You just need someone to help
                                            you clear the board and make the right next move. We dive straight in,
                                            diagnose what&apos;s keeping you stuck and build an actionable game plan you can
                                            use right away.
                                        </p>
                                    </div>
                                    <div className='service-detail-2col-grid'>
                                        <div className='content' id='mdl-node-_962754fb-8120-99c3-36d3-e25b0be9e2d1-68628a74'>
                                            <div className='fade-in-move-on-scroll'>
                                                <div>
                                                    Case acceptance falling flat?
                                                    <br />
                                                    Scheduling chaos?
                                                </div>
                                            </div>
                                        </div>
                                        <div className='content' id='mdl-node-_962754fb-8120-99c3-36d3-e25b0be9e2d5-68628a74'>
                                            <div className='fade-in-move-on-scroll'>
                                                <div>
                                                    Overhead creeping up?
                                                    <br />
                                                    Team culture feeling off?
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='section' id='training'>
                    <div className='container'>
                        <div className='mdl-layout-grid service-detail-grid full-align'>
                            <div className='content' id='mdl-node-_6d0efb59-20ef-0ac9-94a7-e555d30047c5-68628a74'>
                                <div className='service-detail-item' id='merchant-rates'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='service-detail-image-holder'>
                                            <img
                                                className='image-with-animation'
                                                src='/images/czechmate/merchant-rates.jpg'
                                                alt='A credit card held over a card payment terminal'
                                            />
                                        </div>
                                    </div>
                                    <div className='service-detail-content-inside-box'>
                                        <div className='fade-in-move-on-scroll'>
                                            <img className='service-detail-icon' src='/images/chart-infographic-icon.svg' alt='' />
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <h3 className='medium-text no-margins'>Merchant Services Rate Negotiation</h3>
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <p>
                                                Most companies take a percentage of what they save you, for years. Brad
                                                charges a one-time flat fee of $1,495, negotiates better rates with you and
                                                teaches you to monitor them yourself, plus smarter ways to cut what you
                                                process on cards in the first place.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-_185e9a89-cbc8-31d3-56cc-9fef9d6574b5-68628a74'>
                                <div className='service-detail-item' id='treatment-coordinator'>
                                    <div className='fade-in-on-scroll'>
                                        <div className='service-detail-image-holder'>
                                            <img
                                                className='image-with-animation'
                                                src='/images/czechmate/next-move.jpg'
                                                alt='A sticky note reading Next move? beside a white chess knight and a coffee cup'
                                            />
                                        </div>
                                    </div>
                                    <div className='service-detail-content-inside-box'>
                                        <div className='fade-in-move-on-scroll'>
                                            <img className='service-detail-icon' src='/images/connection.svg' alt='' />
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <h3 className='medium-text no-margins'>Treatment Coordinator Training</h3>
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <p>
                                                Six hours of one-on-one coaching, in two 3-hour Zoom sessions, that walk
                                                your TC through every phase of the patient journey. You get our scripts,
                                                forms and workflows, 90 days of support and the option of 1.5 days of
                                                hands-on coaching in your office.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='section-background' />
                </div>

                <div className='section' id='financial-coordinator'>
                    <div className='container'>
                        <div className='mdl-layout-grid service-detail-grid'>
                            <div className='content' id='mdl-node-d1330582-7480-f8f7-c1aa-7f8b4c93643c-68628a74'>
                                <div className='service-detail-content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <img className='service-detail-icon' src='/images/chart-icon.svg' alt='' />
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='no-margins'>Financial Coordinator Training</h2>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <p>
                                            If past-due balances are creeping up or insurance payments are dragging, this
                                            4-hour remote training helps your Financial Coordinator simplify, systematize
                                            and reduce past-dues to under 1%. It&apos;s what we use in our own practice
                                            every day.
                                        </p>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <ul className='list' role='list'>
                                            <li className='list-item-with-checkicon'>
                                                <div>Our scripts, forms, workflows and systems</div>
                                            </li>
                                            <li className='list-item-with-checkicon'>
                                                <div>90 days of support by email, text and phone</div>
                                            </li>
                                            <li className='list-item-with-checkicon'>
                                                <div>Coaching for the doctor on alignment and accountability</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-d1330582-7480-f8f7-c1aa-7f8b4c93644f-68628a74'>
                                <div className='image-holder-with-animation image-holder-service-detail'>
                                    <div className='image-with-animation-container image-service-detail-container'>
                                        <img
                                            className='image-with-animation'
                                            src='/images/czechmate/financial-worksheet.jpg'
                                            alt='An orthodontic financial worksheet tracking past dues at 30, 60 and 90 days'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='section' id='resin-to-retainer'>
                    <div className='container'>
                        <div className='mdl-layout-grid service-detail-grid'>
                            <div className='content' id='mdl-node-e042337a-e920-ab42-bfd2-33c244722d3e-68628a74'>
                                <div className='image-holder-with-animation image-holder-service-detail'>
                                    <div className='image-with-animation-container image-service-detail-container'>
                                        <img
                                            className='image-with-animation'
                                            src='/images/czechmate/resin-to-retainer.jpg'
                                            alt='Resin to Retainer: A 3D Printing Masterclass title card with teacher Bridget Scott'
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className='content' id='mdl-node-e042337a-e920-ab42-bfd2-33c244722d42-68628a74'>
                                <div className='service-detail-content'>
                                    <div className='fade-in-move-on-scroll'>
                                        <img className='service-detail-icon' src='/images/network-cloud.svg' alt='' />
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <h2 className='no-margins'>Resin to Retainer: A 3D Printing Masterclass</h2>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <p>
                                            Tired of guessing your way through 3D printing for your orthodontic models? We
                                            were too. This start-to-finish, no-fluff course takes you from digital scans to
                                            printed models and retainers, exactly the way we do it in our own lab.
                                            <br />
                                            <br />
                                            It&apos;s taught by Bridget Scott, our lead lab tech, and the full course is
                                            $995.
                                        </p>
                                    </div>
                                    <div className='fade-in-move-on-scroll'>
                                        <ul className='list' role='list'>
                                            <li className='list-item-with-checkicon'>
                                                <div>Online and self-paced: start printing in as little as 2 hours</div>
                                            </li>
                                            <li className='list-item-with-checkicon'>
                                                <div>90 days of support by text, email and phone</div>
                                            </li>
                                            <li className='list-item-with-checkicon'>
                                                <div>10% off our EVORetain retainer plastics</div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        </Layout>
    )
}

export default WhatWeDoPage
